/**
 * 整体 Horn 接入技术方案可以参考 https://km.sankuai.com/page/1223580477
 */

import { ENV, getEnv } from '@utils/getEnv';
import request from '@utils/axiosWrapper'; // eslint-disable-line import/no-cycle
import { getCookie } from '@utiljs/cookie';

import { getStorage, setStorage } from '@utils/localStorage'; // eslint-disable-line import/no-cycle

const currentEnv = getEnv();

/**
 * Horn 系统内全局唯一的 key
 *
 * 商家端内多个配置项均已收敛到同一个 Horn key 下。不同的配置项通过同一个配置项内的不同字段分隔
 *
 * https://horn.sankuai.com/files/edit/4502
 */
const HRON_CONFIG_KEY = 'waimai_e_fe_core_horn_config';

/**
 * Horn 配置信息在本地存储中的缓存 KEY
 */
const CONFIG_STORAGE_CACHE_KEY = 'horn_config_cache';

/** Horn 接口返回的原始数据格式 */
type HornRawResponse = {
    /** 用户数据 */
    customer: any;
    /** horn 内部关于此配置项的 meta 信息，主要给 Horn SDK 使用 */
    horn: object;
};

/** 从服务端加载配置项 */
const loadHornConfigFromServer = () => {
    // 非线上环境强制请求 waimaie 线上域名；本地开发环境为了使用本地 mock 文件，使用当前域名即可
    const requestHost =
        currentEnv !== ENV.PRODUCTION && currentEnv !== ENV.LOCALHOST
            ? 'https://waimaie.meituan.com'
            : '';
    const version = 'v1'; // 固定参数
    const os = currentEnv === ENV.PRODUCTION ? 'android' : 'android_test'; // 根据环境固定写死即可
    const from = HRON_CONFIG_KEY; // Horn 中全局唯一Key
    /**
     * Horn服务计算用户ID的入参，可以是任意字符串
     * 计算公式: percentNum=(crc32.ChecksumIEEE(UUID+盐)∗100)/0xFFFFFFFF
     * https://km.sankuai.com/page/874318885
     */
    const id = getCookie('_lxsdk_cuid') || 'hello there?';

    const fe_env = currentEnv; // 自定义参数，标识当前应用运行环境

    return request
        .rawGet<HornRawResponse>(`${requestHost}/horn_config`, {
            params: { version, os, from, fe_env, id },
        })
        .then(res => res.customer)
        .then(data => {
            setStorage(CONFIG_STORAGE_CACHE_KEY, JSON.stringify(data));
        });
};

/**
 * **从本地存储缓存中**获取指定字段的配置内容
 *
 * @param fieldName 字段名
 * @param defaultValue 默认值（本地缓存未读取到缓存配置时返回此默认值）
 */
const getConfigByFieldName = <T>(fieldName: CONFIG_FIELD, defaultValue?: T): T | null => {
    const cachedConfigStr = getStorage(CONFIG_STORAGE_CACHE_KEY);
    let result: T | null = null;

    if (cachedConfigStr !== null) {
        let cachedConfig: any = {};
        try {
            cachedConfig = JSON.parse(cachedConfigStr);
        } catch (e) {
            // Woops~~, Something wrong
        }

        if (typeof cachedConfig[fieldName] !== 'undefined') {
            result = cachedConfig[fieldName] as T;
        }
    }

    // 未从缓存中取到配置且传入了默认值
    if (result === null && typeof defaultValue !== 'undefined') {
        result = defaultValue;
    }

    return result;
};
/**
 * 获取horn配置，如果当前没有horn配置则请求接口获取
 */
const ensureGetHornConfig = () => {
    const cachedConfigStr = getStorage(CONFIG_STORAGE_CACHE_KEY);
    return new Promise(resolve => {
        if (cachedConfigStr === null) {
            loadHornConfigFromServer().then(() => {
                resolve();
            });
        } else {
            resolve();
        }
    });
};
enum CONFIG_FIELD {
    /** 左侧菜单匹配规则 */
    MENU_MATCH_CONFIG = 'menuMatchConfig',
    /** 核心业务页面是否使用 gw 转发 */
    CORE_BUSINESS_USE_GW = 'coreBusinessUseGw',
    /** IM 灰度配置 */
    IM_GRAY_CONFIG = 'imGrayConfig',
    /** 前端路由灰度配置 */
    URL_REPLACE_GRAY_CONFIG = 'urlReplaceGrayConfig',
    /** 是否开启前端静态资源版本更新检测 */
    FE_VERSION_CHECK_ENABLED = 'feVersionCheckEnabled',
    /** 消息提醒白名单配置 */
    NOTIFICATION_CONFIG = 'notificationConfig',
    /** 长任务相关配置 */
    LONG_TASK_CONFIG = 'longTaskConfig',
    /** Raptort自定义指标采样配置。 metricsKeyList中的指标会共享sampleConfig中的采样规则，建议将同一链路的指标放到同一个组中。 */
    RAPTOR_METRICS_SAMPLE_CONFIG = 'raptorMetricsSampleConfig',
    /** llog：logan实时日志上报，白名单配置 */
    LLOG_CONFIG = 'llogConfig',
    /** 迎检白名单 */
    CHECK_ACCT_WHITE_LIST = 'checkAcctWhiteList',
    /** EP SDK 升级灰度 */
    EP_NEW_SDK_GRAY = 'epNewSdkGray',
    /** pc客户端下载入口灰度 */
    PC_CLIENT_DOWNLOAD_GRAY = 'pcClientDownloadGray',
    /** pc客户端登录页更新灰度 */
    PC_CLIENT_LOGIN_UPDATE_GRAY = 'pcClientLoginUpdateGray',
    /** Push/轮询改造灰度 */
    PUSH_REFACTOR_GRAY = 'pushRefactorGray',
    /** 消息中心预览文案过滤配置 */
    BUSINESS_MESSAGE_PREVIEW_CONFIG = 'messagePreviewsToFilter',
    /** 是否开启scIndex失败后踢蹬 */
    SC_INDEX_CONFIG = 'scIndexLogout',
    /** 课堂白名单门店(展示用) */
    KANGAROO_COLLEGE_WHITE_LIST = 'kangarooCollegeWhiteList',
}

const Horn = {
    loadHornConfigFromServer,
    getConfigByFieldName,
    CONFIG_FIELD,
    ensureGetHornConfig,
};

export default Horn;
